import { DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  asapScheduler,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  fromEvent,
  map,
  merge,
  mergeMap,
  of,
  ReplaySubject,
  share,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { Rect } from '../../helpers/coords/rect';
import { RxUtils } from '../../utils/rx.utils';

type VerticalAlignment = 'top' | 'bottom' | 'center';
type HorizontalAlignment = 'left' | 'right' | 'center';

@Directive({
  selector: 'ng-template[tooltipContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-tooltip',
  templateUrl: 'tooltip.component.html',
  styleUrls: ['tooltip.component.scss'],
})
export class TooltipComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild('tooltipEl', { static: false })
  tooltipElRef: ElementRef<HTMLDivElement>;

  @Input()
  visible = false;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Output()
  onContentReady = new EventEmitter<void>();

  @Input()
  disabled = false;

  @Input()
  delay = 0;

  @Input()
  target: HTMLElement;
  @Input()
  container: HTMLElement;

  @Input()
  verticalAlignment: VerticalAlignment = 'top';
  @Input()
  horizontalAlignment: HorizontalAlignment = 'center';

  private _contentReadySubject = new ReplaySubject<void>(1);

  private targetTriggerSubscription: Subscription;
  private resizeSubscription: Subscription;
  private visibleInternalSubject = new Subject<void>();

  private lifecycle = new Subject<void>();

  constructor(@Inject(DOCUMENT) private _document: Document) {}

  ngOnInit(): void {
    this._contentReadySubject
      .pipe(first(), takeUntil(this.lifecycle))
      .subscribe(() => {
        const tooltipEl = this.tooltipElRef.nativeElement;
        RxUtils.observeElementResize(tooltipEl)
          .pipe(
            map(([entry]) => Rect.fromObject(entry.contentRect)),
            startWith(Rect.fromElement(tooltipEl)),
            distinctUntilChanged((prev, curr) => {
              return prev.width === curr.width && prev.height === curr.height;
            }),
            takeUntil(this.lifecycle)
          )
          .subscribe((rect) => {
            tooltipEl.style.setProperty(
              '--tooltip-real-width',
              rect.width + 'px'
            );
            tooltipEl.style.setProperty(
              '--tooltip-real-height',
              rect.height + 'px'
            );
          });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.target || changes.delay || changes.disabled) {
      this.handleTargetTrigger();
    }

    if (changes.target || changes.container || changes.disabled) {
      this.handleResize();
    }

    if (changes.visible && this.visible) {
      this.visibleInternalSubject.next();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onContentReadyInternal() {
    this._contentReadySubject.next();

    this.onContentReady.emit();
  }

  private handleTargetTrigger() {
    this.targetTriggerSubscription?.unsubscribe();

    if (!this.target || this.disabled) {
      return;
    }

    const shouldShowTooltipObservableFn = (el: Element) => {
      return merge(
        merge(fromEvent(el, 'mouseenter'), fromEvent(el, 'touchstart')).pipe(
          map(() => true)
        ),
        merge(
          fromEvent(el, 'mouseleave'),
          fromEvent(el, 'touchend'),
          fromEvent(el, 'touchcancel')
        ).pipe(map(() => false))
      );
    };

    const tooltipObservable = this._contentReadySubject.pipe(
      first(),
      mergeMap(() => {
        const tooltipEl = this.tooltipElRef.nativeElement;
        return shouldShowTooltipObservableFn(tooltipEl);
      })
    );

    this.targetTriggerSubscription = merge(
      shouldShowTooltipObservableFn(this.target),
      tooltipObservable
    )
      .pipe(
        distinctUntilChanged(),
        debounceTime(this.delay ?? 0, asapScheduler),
        takeUntil(this.lifecycle)
      )
      .subscribe((visible) => {
        this.visible = visible;
        this.visibleChange.emit(this.visible);
      });
  }

  private handleResize() {
    this.resizeSubscription?.unsubscribe();

    if (this.disabled) {
      return;
    }

    this.resizeSubscription = this._contentReadySubject
      .pipe(
        first(),
        mergeMap(() => {
          const defaultObservable = merge(
            merge(
              this.visibleInternalSubject.pipe(map(() => this.visible)),
              this.visibleChange
            ).pipe(
              distinctUntilChanged(),
              filter((a) => a)
            ),
            fromEvent(window, 'resize')
          ).pipe(share());

          const container = this.container ?? this._document.body;
          const containerResizeObservable = merge(
            RxUtils.observeElementResize(container),
            defaultObservable
          ).pipe(
            map(() => Rect.fromElement(container)),
            startWith(Rect.fromElement(container)),
            distinctUntilChanged((prev, curr) => {
              return prev.equals(curr);
            })
          );

          const targetResizeObservable = !this.target
            ? of(Rect.empty)
            : merge(
                RxUtils.observeElementResize(this.target),
                defaultObservable
              ).pipe(
                map(() => Rect.fromElement(this.target)),
                startWith(Rect.fromElement(this.target)),
                distinctUntilChanged((prev, curr) => {
                  return prev.equals(curr);
                })
              );

          return combineLatest(
            targetResizeObservable,
            containerResizeObservable
          );
        }),
        debounceTime(0, asapScheduler),
        takeUntil(this.lifecycle)
      )
      .subscribe(([targetRect, containerRect]) => {
        this.handlePos(targetRect, containerRect);
      });
  }

  private handlePos(targetRect: Rect, containerRect: Rect) {
    targetRect = targetRect.clone();
    targetRect.relativeTo(containerRect.pos());

    const tooltipEl = this.tooltipElRef.nativeElement;
    tooltipEl.style.setProperty('--tooltip-target-pos-x', targetRect.x + 'px');
    tooltipEl.style.setProperty('--tooltip-target-pos-y', targetRect.y + 'px');
    tooltipEl.style.setProperty(
      '--tooltip-target-width',
      targetRect.width + 'px'
    );
    tooltipEl.style.setProperty(
      '--tooltip-target-height',
      targetRect.height + 'px'
    );

    tooltipEl.style.setProperty(
      '--tooltip-container-width',
      containerRect.width + 'px'
    );
    tooltipEl.style.setProperty(
      '--tooltip-container-height',
      containerRect.height + 'px'
    );
  }
}
